import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { SchedulePage } from './SchedulePage';
import { LoginScreen, UserProfile } from './components/LoginScreen';
import { VendorDashboard } from './components/VendorDashboard';
// ChartViewer removed - using direct cronograma route instead
import { HomePage } from './components/HomePage';
import { WizardPage } from './components/WizardPage';
import { LossesDelaysPage } from './components/LossesDelaysPage';
import { initializeAuth, logout as apiLogout, UserInfo } from './utils/authApi';

// Auth context for managing user state
export const AuthContext = React.createContext<{
  userProfile: UserProfile | null;
  userInfo: UserInfo | null;
  setUserProfile: (profile: UserProfile | null) => void;
  setUserInfo: (userInfo: UserInfo | null) => void;
  isInitializing: boolean;
}>({
  userProfile: null,
  userInfo: null,
  setUserProfile: () => { },
  setUserInfo: () => { },
  isInitializing: true
});

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredRole?: UserProfile }> = ({
  children,
  requiredRole
}) => {
  const { userProfile, isInitializing } = React.useContext(AuthContext);

  // Show loading while initializing auth
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-dexco-gray-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-dexco-green-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-dexco-text-dark font-body">Verificando autenticação...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return <Navigate to="/" replace />;
  }

  if (requiredRole && userProfile !== requiredRole) {
    return <Navigate to={userProfile === 'diretor' ? '/home' : '/dashboard'} replace />;
  }

  return <>{children}</>;
};

// Main App Router
const AppRoutes: React.FC = () => {
  const { userProfile, setUserProfile, setUserInfo, isInitializing } = React.useContext(AuthContext);
  const navigate = useNavigate();

  const handleLogin = (profile: UserProfile, userInfo: UserInfo) => {
    setUserProfile(profile);
    setUserInfo(userInfo);
    if (profile === 'diretor') {
      navigate('/home');
    } else {
      navigate('/dashboard');
    }
    // Toast is already shown in LoginScreen
  };

  const handleLogout = async () => {
    try {
      await apiLogout();
      setUserProfile(null);
      setUserInfo(null);
      navigate('/');
      toast.success('Logout realizado com sucesso');
    } catch (error) {
      console.error('Logout error:', error);
      // Still navigate away even if API call fails
      setUserProfile(null);
      setUserInfo(null);
      navigate('/');
      toast.success('Logout realizado com sucesso');
    }
  };

  // Show loading during auth initialization
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-dexco-gray-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-dexco-green-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-dexco-text-dark font-body">Inicializando aplicação...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={
        userProfile ? (
          <Navigate to={userProfile === 'diretor' ? '/home' : '/dashboard'} replace />
        ) : (
          <LoginScreen onLogin={handleLogin} />
        )
      } />

      {/* Chart viewer route removed - using cronograma/:taskId instead */}

      {/* Protected routes */}
      <Route path="/home" element={
        <ProtectedRoute requiredRole="diretor">
          <HomePage onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/wizard" element={
        <ProtectedRoute requiredRole="diretor">
          <WizardPage onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/losses-delays" element={
        <ProtectedRoute requiredRole="diretor">
          <LossesDelaysPage onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/dashboard" element={
        <ProtectedRoute>
          <VendorDashboard onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/schedule" element={
        <ProtectedRoute>
          <SchedulePage onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      <Route path="/cronograma/:taskId" element={
        <ProtectedRoute>
          <SchedulePage onLogout={handleLogout} />
        </ProtectedRoute>
      } />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export const App: React.FC = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Initialize authentication on app startup
  useEffect(() => {
    const initAuth = async () => {
      try {
        const user = await initializeAuth();

        if (user) {
          setUserProfile(user.profile_type);
          setUserInfo(user);
          console.log('✅ Auth initialized for user:', user.profile_type);
        } else {
          console.log('🔐 No valid session found');
        }
      } catch (error) {
        console.error('❌ Auth initialization failed:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initAuth();
  }, []);

  // Listen for auth expiration events
  useEffect(() => {
    const handleAuthExpired = () => {
      console.warn('🔐 Auth expired event received, logging out user');
      setUserProfile(null);
      setUserInfo(null);
      toast.error('Sessão expirada. Por favor, faça login novamente.');
    };

    window.addEventListener('auth-expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, []);

  return (
    <Router>
      <AuthContext.Provider value={{
        userProfile,
        userInfo,
        setUserProfile,
        setUserInfo,
        isInitializing
      }}>
        <AppRoutes />
      </AuthContext.Provider>
    </Router>
  );
};
